import React from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import { useMediaQuery } from '@mantine/hooks'
import dayjs from 'dayjs'
import 'dayjs/locale/ko'
import {
  learningSupportList,
  projectDescList,
  programDescList,
  projectOutputList,
  curriculumDetailList,
  reviewList,
  benefitSlideList,
  applyScheduleList,
  bootCampFaqList,
  bootCampFaqTabList,
  firstCompanyList,
  secondCompanyList,
} from '../../../../constants/kdt-backendj-17th'
import Link from 'next/link'
import Image from 'next/image'
import ReactPlayer from 'react-player'
import { useMemo, useState } from 'react'
import { useCopyUrlShare } from 'shared/hooks/useCopyUrlShare'
import LearningSupportComparison from './LearningSupportComparison'
import ProjectTab from './ProjectTab'
import CurriculumAccordion from './CurriculumAccordion'
import CurriculumTab from './CurriculumTab'
import ProgramTab from './ProgramTab'
import BenefitAccordion from './BenefitAccordion'
import { ProjectOutputSliderItem, ReviewSliderItem, BenefitSliderItem } from './SliderItem'
import styles from './kdt-bebj-17th.module.scss'
import { schoolNavList, ScrollNav } from './ScrollNav'
import LazyLoadSection from '@/components/LazyLoadSection'

interface IBebj17thProps {
  applyStartedAt: string
  applyEndedAt: string
  startedAt: string
  endedAt: string
  totalSeconds: number
}

const Bebj17th = React.memo(
  ({ applyStartedAt, applyEndedAt, startedAt, endedAt, totalSeconds }: IBebj17thProps) => {
    const isMobile = useMediaQuery('(max-width: 1023px)')
    const [faqTab, setFaqTab] = useState('apply')
    const allFaqs = [
      ...bootCampFaqList.apply,
      ...bootCampFaqList.nebeka,
      ...bootCampFaqList.employment,
      ...bootCampFaqList.edu,
      ...bootCampFaqList.benefit,
      ...bootCampFaqList.etc,
    ]
    const faqList = faqTab === 'all' ? allFaqs : bootCampFaqList[faqTab]
    const onClickFaqTab = (value: string) => {
      setFaqTab(value)
    }
    const earlySessionDate = new Date('2025-05-08 21:00')
    const isAfterEarlySessionDate = useMemo(() => dayjs().isAfter(earlySessionDate), [])
    const { copyUrlShare } = useCopyUrlShare()

    dayjs.locale('ko')
    const convertAmPm = (time: string) => {
      return time.replace('AM', '오전').replace('PM', '오후')
    }
    const applyStart = convertAmPm(dayjs(applyStartedAt).format('YY.MM.DD A h시'))
    const applyEnd = convertAmPm(dayjs(applyEndedAt).format('YY.MM.DD A h시'))
    const applyBottomStart = convertAmPm(dayjs(applyStartedAt).format('MM월 DD일(dd) A h시'))
    const applyBottomEnd = convertAmPm(dayjs(applyEndedAt).format('MM월 DD일(dd) A h시'))
    const start = dayjs(startedAt).format('YY.MM.DD')
    const end = dayjs(endedAt).format('YY.MM.DD')
    const startBottom = dayjs(startedAt).format('YYYY년 MM월 DD일')
    const endBottom = dayjs(endedAt).format('YYYY년 MM월 DD일')
    const totalHours = Math.ceil(totalSeconds / 3600)

    return (
      <div>
        {/* Section1_키비주얼 */}
        <section className="bg-gradient-to-b from-[#FFF3EB] to-[#FFE0C7]">
          <div className={`${styles.section1_kv} relative h-full w-full`}>
            <video
              src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section1_kv_video_${
                isMobile ? 'm' : 'pc'
              }.mp4`}
              muted
              autoPlay
              playsInline
              loop
            ></video>
          </div>
        </section>

        {/* Section2_핵심포인트 */}
        <LazyLoadSection
          height={753}
          mobileHeight={863}
          className="bg-gradient-to-b from-gray-900 to-trueGray-800 pb-[42px] pt-6 lg:py-20"
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
            <ul className="flex w-full flex-col gap-3 lg:flex-row lg:flex-wrap lg:gap-5">
              <li
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="100"
                className="flex flex-1 flex-row items-center justify-center gap-3 rounded-lg bg-trueGray-800 px-4 py-3 lg:gap-5 lg:px-6 lg:py-5"
              >
                <img
                  className="h-16 w-16 object-cover"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section2_icon1.png"
                  alt="-"
                />
                <div className="flex flex-1 flex-col items-start justify-center">
                  <p className="mb-1 rounded bg-[rgba(255,119,16,0.12)] px-2 py-1 text-xs font-semibold text-yellow-450 lg:mb-2 lg:px-3 lg:font-bold">
                    POINT 1
                  </p>
                  <p
                    className={`${styles.section2_mediaquery} text-sm leading-normal text-label-assistive lg:text-base`}
                  >
                    2번의 인턴십으로 쌓는
                    <br />
                    기업 실무 경험
                  </p>
                </div>
              </li>
              <li
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="300"
                className="flex flex-1 flex-row items-center justify-center gap-3 rounded-lg bg-trueGray-800 px-4 py-3 lg:gap-5 lg:px-6 lg:py-5"
              >
                <img
                  className="h-16 w-16 object-cover"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section2_icon2.png"
                  alt="-"
                />
                <div className="flex flex-1 flex-col items-start justify-center">
                  <p className="mb-1 rounded bg-[rgba(255,119,16,0.12)] px-2 py-1 text-xs font-semibold text-yellow-450 lg:mb-2 lg:px-3 lg:font-bold">
                    POINT 2
                  </p>
                  <p
                    className={`${styles.section2_mediaquery} text-sm leading-normal text-label-assistive lg:text-base`}
                  >
                    실무 흐름 그대로 설계된
                    <br />
                    취업 특화 Java 커리큘럼
                  </p>
                </div>
              </li>
              <li
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="500"
                className="flex flex-1 flex-row items-center justify-center gap-3 rounded-lg bg-trueGray-800 px-4 py-3 lg:gap-5 lg:px-6 lg:py-5"
              >
                <img
                  className="h-16 w-16 object-cover"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section2_icon3.png"
                  alt="-"
                />

                <div className="flex flex-1 flex-col items-start justify-center">
                  <p className="mb-1 rounded bg-[rgba(255,119,16,0.12)] px-2 py-1 text-xs font-semibold text-yellow-450 lg:mb-2 lg:px-3 lg:font-bold">
                    POINT 3
                  </p>
                  <p
                    className={`${styles.section2_mediaquery} text-sm leading-normal text-label-assistive lg:text-base`}
                  >
                    실제 기업 프로젝트 참여로
                    <br />
                    포트폴리오 강화
                  </p>
                </div>
              </li>
            </ul>
            <ul
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="700"
              className="my-12 flex w-full flex-col lg:my-5"
            >
              <li className="flex w-full flex-row items-start border-b border-[#404040] pb-3 lg:items-center lg:pb-4">
                <h6 className="flex w-[126px] flex-row items-center justify-start gap-3 text-sm font-semibold text-white lg:w-[calc(33.33%+16px)] lg:gap-4 lg:text-base">
                  <img
                    className="h-6 w-6 object-contain"
                    src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section2_icon1_ver2.png"
                    alt="-"
                  />
                  모집기간
                </h6>
                <p className="text-sm text-label-assistive lg:w-2/3 lg:text-base">
                  {applyStart} ~ <br className="block lg:hidden" />
                  {applyEnd}
                </p>
              </li>
              <li className="flex w-full flex-row items-center border-b border-[#404040] py-3 lg:py-4">
                <h6 className="flex w-[126px] flex-row items-center justify-start gap-3 text-sm font-semibold text-white lg:w-[calc(33.33%+16px)] lg:gap-4 lg:text-base">
                  <img
                    className="h-6 w-6 object-contain"
                    src={
                      'https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section2_icon2_ver2.png'
                    }
                    alt="-"
                  />
                  교육기간
                </h6>
                <p className="text-sm text-label-assistive lg:w-2/3 lg:text-base">
                  {start} ~ {end}
                </p>
              </li>
              <li className="flex w-full flex-row items-start pt-3 lg:pt-4">
                <h6 className="flex w-[126px] flex-row items-center justify-start gap-3 text-sm font-semibold text-white lg:w-[calc(33.33%+16px)] lg:gap-4 lg:text-base">
                  <img
                    className="h-6 w-6 object-contain"
                    src={
                      'https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section2_icon3_ver2.png'
                    }
                    alt="-"
                  />
                  교육비용
                </h6>
                <p className="text-sm text-label-assistive lg:w-2/3 lg:text-base">
                  <b>내일배움카드 소지 시 전액 무료</b>
                  <br />
                  <span className="text-xs leading-normal lg:text-sm">
                    *매월 최대 81만 원의 지원금 신청 가능
                  </span>
                </p>
              </li>
            </ul>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="900"
              className="flex w-full flex-row flex-wrap items-center justify-center rounded-lg border border-yellow-450 bg-[rgba(255,119,16,0.10)] px-4 py-3 lg:px-6 lg:py-5"
            >
              <div className="mb-3 flex w-full flex-row items-center justify-start gap-3 border-b border-label-neutral pb-3 lg:mb-[14px] lg:gap-5 lg:pb-[14px]">
                <img
                  className="h-16 w-16 object-cover"
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-15th/section2_icon4.png"
                  alt="-"
                />
                <p className="text-sm font-semibold text-white lg:text-[28px] lg:font-bold">
                  지원조건
                </p>
              </div>
              <ul className={`${styles.section2_ul} flex w-full flex-col lg:gap-2`}>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section2} text-sm leading-normal text-label-assistive lg:text-lg`}
                >
                  국민내일배움카드 보유자 혹은 발급 가능하신 분
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section2} text-sm leading-normal text-label-assistive lg:text-lg`}
                >
                  평일 오전 9시부터 오후 6시까지 수업 참여가 <br className="block lg:hidden" />
                  가능하신 분
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section2} text-sm leading-normal text-label-assistive lg:text-lg`}
                >
                  직장 혹은 학교 등과 병행하지 않고 수업에만 집중 <br className="block lg:hidden" />
                  가능하신 분
                </li>
                <li
                  className={`${styles.text_dot} ${styles.text_dot_section2} text-sm leading-normal text-label-assistive lg:text-lg`}
                >
                  국내에 거주하고 계신 분
                </li>
              </ul>
            </div>
          </div>
        </LazyLoadSection>

        <ScrollNav schoolNavList={schoolNavList} />

        {/* Section3_커리큘럼 */}
        <LazyLoadSection
          id="curriculum"
          height={2043}
          mobileHeight={1684}
          className="bg-gray-900 pb-[116px] pt-[88px] lg:pb-[130px] lg:pt-[140px]"
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="mb-10 flex flex-col items-center justify-center lg:mb-20"
            >
              <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">커리큘럼</p>
              <h4 className="mb-5 text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
                실전에서 통하는 기술을 <br className="block lg:hidden" />
                채우는 <br className="hidden lg:block" />
                취업 특화 커리큘럼
              </h4>
              <p className="text-center text-sm leading-normal text-label-assistive lg:text-lg">
                Java와 Spring을 중심으로 백엔드 실무 전 과정을 따라가며
                <br />
                실제 서비스 개발에 통하는 기술을 단계별로 익혀요.
              </p>
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="mb-[60px] lg:mb-[100px]"
            >
              <h6 className="mb-5 text-center text-lg font-bold text-label-alternative lg:mb-8 lg:text-2xl">
                커리큘럼 POINT
              </h6>
              <ul className="flex flex-col gap-3 lg:flex-row lg:flex-wrap lg:gap-5">
                <li className="flex w-full flex-col gap-1 rounded-lg bg-trueGray-800 px-4 py-3 lg:w-[calc(50%-12px)] lg:gap-5 lg:px-5 lg:py-6">
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.12)] px-2 py-1 text-xs font-semibold text-yellow-450 lg:px-3 lg:text-sm">
                    POINT 1
                  </p>
                  <p className="text-sm text-label-assistive lg:text-lg lg:font-medium">
                    기초-응용-심화-프로젝트 순으로 진행되는
                    <br />
                    실무 스킬 중심의 단계별 성장 커리큘럼
                  </p>
                </li>
                <li className="flex w-full flex-col gap-1 rounded-lg bg-trueGray-800 px-4 py-3 lg:w-[calc(50%-12px)] lg:gap-5 lg:px-5 lg:py-6">
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.12)] px-2 py-1 text-xs font-semibold text-yellow-450 lg:px-3 lg:text-sm">
                    POINT 2
                  </p>
                  <p className="text-sm text-label-assistive lg:text-lg lg:font-medium">
                    실무 능력 배양을 위한 Java&Spring 특화 커리큘럼과
                    <br />
                    실제 현업 프로젝트(개인+팀) 참여
                  </p>
                </li>
                <li className="flex w-full flex-col gap-1 rounded-lg bg-trueGray-800 px-4 py-3 lg:gap-5 lg:px-5 lg:py-6">
                  <p className="max-w-fit rounded bg-[rgba(255,119,16,0.12)] px-2 py-1 text-xs font-semibold text-yellow-450 lg:px-3 lg:text-sm">
                    POINT 3
                  </p>
                  <p className="text-sm text-label-assistive lg:text-lg lg:font-medium">
                    각 파트별 미니 프로젝트 진행을 통해 이론 및 <br className="block lg:hidden" />
                    Java 웹 프로그래밍 복습
                  </p>
                  <div className="flex flex-col gap-1 border-t border-[#444] pt-1 lg:gap-5 lg:pt-5">
                    <p className="text-sm text-label-assistive lg:text-base lg:font-medium">
                      [미니 프로젝트 예시]
                    </p>
                    <ul className="flex flex-col gap-1 lg:gap-2">
                      <li
                        className={`${styles.text_dot} text-sm leading-normal text-label-assistive lg:text-base lg:font-medium`}
                      >
                        콘솔 기반에서 실행되는 간단한 프로그래밍 만들기
                        <br />
                        (ex.계산기, ATM 시뮬레이터, 숫자 퀴즈 등)
                      </li>
                      <li
                        className={`${styles.text_dot} text-sm leading-normal text-label-assistive lg:text-base lg:font-medium`}
                      >
                        콘솔 기반의 스레드를 활용한 프로그래밍 만들기
                        <br />
                        (ex. 가계부 프로그램, 채팅 시뮬레이터 등)
                      </li>
                      <li
                        className={`${styles.text_dot} text-sm leading-normal text-label-assistive lg:text-base lg:font-medium`}
                      >
                        MVC 패턴 및 Thymeleaf 템플릿을 활용한 간단한 웹 프로젝트 (ex. 온라인 투표
                        시스템, 책 리뷰 게시판 등)
                      </li>
                    </ul>
                  </div>
                </li>
              </ul>
            </div>

            <h6
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="mb-5 text-center text-lg font-bold text-label-alternative lg:mb-8 lg:text-2xl"
            >
              커리큘럼 자세히 보기
            </h6>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className={`${styles.curriculum_accordion_wrap} hidden flex-row overflow-hidden rounded-lg bg-trueGray-800 lg:flex`}
            >
              <CurriculumTab curriculumDetailList={curriculumDetailList} />
            </div>
            <ul
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className={`${styles.curriculum_accordion_wrap} flex flex-col overflow-hidden lg:hidden`}
            >
              {curriculumDetailList.map((curriculum, idx) => (
                <CurriculumAccordion contents={curriculum} key={curriculum.titleM} idx={idx} />
              ))}
            </ul>
          </div>
        </LazyLoadSection>

        {/* Section_add1_수준별 교육 지원 */}
        <LazyLoadSection
          height={846}
          mobileHeight={834}
          className="bg-trueGray-800 pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-36"
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="mb-10 flex flex-col items-center justify-center lg:mb-16"
            >
              <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">수준별 교육 지원</p>
              <h4 className="mb-5 text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
                입문자도, 경험자도 <br className="block lg:hidden" />
                맞춤형 교육 지원으로 취업까지!
              </h4>
              <p className="text-center text-sm leading-normal text-label-assistive lg:text-lg">
                기초가 부족한 입문자에겐 이론 및 개념부터, <br className="block lg:hidden" />
                심화를 원하는 경험자에겐 <br className="hidden lg:block" />더 깊은 실전 역량을
                채우는 <br className="block lg:hidden" />
                맞춤형 교육 프로그램을 지원해요.
              </p>
            </div>
            <ul
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="flex flex-col gap-3 lg:flex-row lg:gap-6"
            >
              <li className="flex w-full flex-col gap-1 rounded-lg bg-[#303030] px-4 py-3 lg:w-auto lg:flex-1 lg:gap-3 lg:px-5 lg:py-5">
                <p className="max-w-fit rounded bg-[rgba(255,119,16,0.12)] px-2 py-1 text-xs font-semibold text-yellow-450 lg:text-sm">
                  입문자를 위한 프로그램
                </p>
                <p className="text-base font-bold text-white lg:text-lg lg:font-semibold">
                  처음 배우는 사람도 개념부터 실습까지 익혀요
                </p>
                <ul className="flex flex-col gap-1 lg:gap-2">
                  <li
                    className={`${styles.text_dot} ${styles.text_dot_add1} text-sm leading-normal text-label-assistive lg:text-base`}
                  >
                    지원 완료자 대상 유료 VOD 강의 제공(백엔드 Java 첫걸음 시작하기)
                  </li>
                  <li
                    className={`${styles.text_dot} ${styles.text_dot_add1} text-sm leading-normal text-label-assistive lg:text-base`}
                  >
                    백엔드 기초를 다지는 프리러닝 자료 제공
                  </li>
                  <li
                    className={`${styles.text_dot} ${styles.text_dot_add1} text-sm leading-normal text-label-assistive lg:text-base`}
                  >
                    2개월 간 기초반 운영으로 Java 기초 개념 학습
                  </li>
                  <li
                    className={`${styles.text_dot} ${styles.text_dot_add1} text-sm leading-normal text-label-assistive lg:text-base`}
                  >
                    눈높이에 맞춘 반복 학습과 기초 알고리즘 연습
                  </li>
                </ul>
              </li>
              <li className="flex w-full flex-col gap-1 rounded-lg bg-[#303030] px-4 py-3 lg:w-auto lg:flex-1 lg:gap-3 lg:px-5 lg:py-5">
                <p className="max-w-fit rounded bg-[rgba(255,119,16,0.12)] px-2 py-1 text-xs font-semibold text-yellow-450 lg:text-sm">
                  경험자를 위한 프로그램
                </p>
                <p className="text-base font-bold text-white lg:text-lg lg:font-semibold">
                  기초를 넘어 실전 감각과 해결 능력을 키워요
                </p>
                <ul className="flex flex-col gap-1 lg:gap-2">
                  <li
                    className={`${styles.text_dot} ${styles.text_dot_add1} text-sm leading-normal text-label-assistive lg:text-base`}
                  >
                    '자바로 학습하는 컴퓨터 과학' 자료 및 강의로 CS 핵심 개념 학습
                  </li>
                  <li
                    className={`${styles.text_dot} ${styles.text_dot_add1} text-sm leading-normal text-label-assistive lg:text-base`}
                  >
                    실전 감각을 키우는 다양한 알고리즘 문제 제공
                  </li>
                  <li
                    className={`${styles.text_dot} ${styles.text_dot_add1} text-sm leading-normal text-label-assistive lg:text-base`}
                  >
                    빠른 성장을 위한 심화 학습 루트 지원
                  </li>
                </ul>
              </li>
            </ul>
            <p
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="mt-3 text-center text-xs text-label-assistive lg:mt-10 lg:text-base"
            >
              * 전공 여부와 상관없이, 자신의 수준에 맞는 프로그램을 선택해{' '}
              <br className="block lg:hidden" />
              참여할 수 있습니다.
            </p>
          </div>
        </LazyLoadSection>

        {/* Section4_프로젝트 */}
        <LazyLoadSection
          id="project"
          height={3216}
          mobileHeight={2423}
          className="bg-[#E5E5E5] pb-[104px] pt-[88px] lg:pb-[180px] lg:pt-[104px]"
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="mb-10 flex flex-col items-center justify-center lg:mb-20"
            >
              <p className="mb-2 text-sm font-bold text-yellow-450 lg:mb-0 lg:text-lg">프로젝트</p>
              <h4 className="mb-5 text-center text-2xl font-bold leading-snug text-label-normal lg:text-4xl lg:leading-[1.4]">
                경력같은 신입의 역량을 채우는
                <br />
                실제 기업 프로젝트 참여
              </h4>
              <p className="text-center text-sm leading-normal text-label-neutral lg:text-lg">
                백엔드 개발자 취업에서 가장 중요한 것은? 바로 실무 경험!{' '}
                <br className="block lg:hidden" />
                기업이 직접 제안한 과제를 <br className="hidden lg:block" />
                바탕으로 실무와 동일한 과정을 <br className="block lg:hidden" />
                따라가며 프로젝트를 완성하고, 포트폴리오를 강화해요.
              </p>
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="mb-[60px] flex w-full flex-col items-center justify-center gap-5 lg:mb-[120px]"
            >
              <h6 className="text-center text-lg font-bold text-label-normal lg:hidden">
                심사 평가서
              </h6>
              <div className="flex w-full flex-col items-center justify-center gap-4 rounded-lg border bg-white px-4 py-6 lg:flex-row lg:gap-10 lg:px-8 lg:py-[30px]">
                <div className="w-full shrink-0 lg:w-[41.78%]">
                  <Image
                    src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section4_img1_${
                      isMobile ? 'm' : 'pc'
                    }.png`}
                    layout="fill"
                    objectFit="cover"
                    className="!relative h-full w-full"
                    alt="-"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-[28px]">
                  <h6 className="hidden text-2xl font-bold text-label-normal lg:block">
                    심사 평가서
                  </h6>
                  <ul className="flex flex-col gap-1">
                    <li
                      className={`${styles.text_dot} ${styles.text_dot_section4} text-sm leading-normal text-label-normal lg:text-lg`}
                    >
                      프로젝트에 참여한 수강생에게는 해당 기업의 현직 실무자가 직접 작성한{' '}
                      <br className="hidden lg:block" />
                      프로젝트 심사 평가서를 제공해요.
                    </li>
                    <li
                      className={`${styles.text_dot} ${styles.text_dot_section4} text-sm leading-normal text-label-normal lg:text-lg`}
                    >
                      단순한 수료증이 아닌, 기획·구현·협업·기술 완성도 등 실무 항목별 평가가 포함된
                      공식 피드백 자료로 지원 시 포트폴리오와 함께 제출할 수 있는 강력한 취업 증빙
                      자료로 활용할 수 있어요.
                    </li>
                  </ul>
                </div>
              </div>
            </div>

            <h6
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="mb-5 flex flex-col text-center text-lg font-bold text-label-normal lg:mb-10 lg:text-2xl"
            >
              참여 기업
              <span className="mt-3 text-center text-sm font-normal text-label-neutral lg:mt-7 lg:text-lg">
                우수 수료생에게는 참여 기업 인턴십 기회를 제공해요.
              </span>
            </h6>
            <div className="mb-[60px] flex flex-row items-center justify-center gap-[14px] lg:mb-[120px] lg:gap-10">
              <div
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="500"
                className="flex flex-col items-center justify-center gap-2 lg:gap-4"
              >
                <div className="flex h-[100px] w-[100px] shrink-0 items-center justify-center rounded-full bg-white shadow-[0px_0px_18.182px_0px_rgba(102,102,102,0.02),_0px_0px_9.545px_0px_rgba(102,102,102,0.01),_0px_0px_7.727px_0px_rgba(102,102,102,0.03)] lg:h-[220px] lg:w-[220px]">
                  <div className="w-[84.25px] lg:w-[185.35px]">
                    <Image
                      src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section4_logo1.png"
                      layout="fill"
                      objectFit="contain"
                      className="!relative w-full"
                      alt="참여기업 모멘티 기업로고"
                    />
                  </div>
                </div>
                <p className="text-sm font-bold text-label-neutral lg:text-lg">모멘티</p>
              </div>
              <div
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="700"
                className="flex flex-col items-center justify-center gap-2 lg:gap-4"
              >
                <div className="flex h-[100px] w-[100px] shrink-0 items-center justify-center rounded-full bg-white shadow-[0px_0px_18.182px_0px_rgba(102,102,102,0.02),_0px_0px_9.545px_0px_rgba(102,102,102,0.01),_0px_0px_7.727px_0px_rgba(102,102,102,0.03)] lg:h-[220px] lg:w-[220px]">
                  <div className="w-[66px] lg:w-[145.4px]">
                    <Image
                      src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section4_logo2.png"
                      layout="fill"
                      objectFit="contain"
                      className="!relative w-full"
                      alt="참여기업 카이토리 기업로고"
                    />
                  </div>
                </div>
                <p className="text-sm font-bold text-label-neutral lg:text-lg">카이토리</p>
              </div>
              <div
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="900"
                className="flex flex-col items-center justify-center gap-2 lg:gap-4"
              >
                <div className="flex h-[100px] w-[100px] shrink-0 items-center justify-center rounded-full bg-white shadow-[0px_0px_18.182px_0px_rgba(102,102,102,0.02),_0px_0px_9.545px_0px_rgba(102,102,102,0.01),_0px_0px_7.727px_0px_rgba(102,102,102,0.03)] lg:h-[220px] lg:w-[220px]">
                  <div className="w-[59.74px] lg:w-[131.44px]">
                    <Image
                      src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section4_logo3.png"
                      layout="fill"
                      objectFit="contain"
                      className="!relative w-full"
                      alt="참여기업 하들소프트 기업로고"
                    />
                  </div>
                </div>
                <p className="text-sm font-bold text-label-neutral lg:text-lg">하들소프트</p>
              </div>
            </div>

            <h6
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="mb-5 text-center text-lg font-bold text-label-normal lg:mb-8 lg:text-2xl"
            >
              프로젝트
            </h6>
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="500"
            >
              <ProjectTab projectDetailList={projectDescList} />
            </div>
          </div>
        </LazyLoadSection>

        {/* Section5_수료생 결과물 */}
        <LazyLoadSection
          height={1287}
          mobileHeight={546}
          className="bg-strong pb-[86px] pt-20 lg:pb-40 lg:pt-36"
        >
          <div className="mx-auto w-full">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-20 lg:gap-5"
            >
              <h4 className="text-center text-2xl font-bold text-label-normal lg:text-4xl">
                수료생 프로젝트 결과물
              </h4>
              <p className="text-center text-sm leading-normal text-label-neutral lg:text-lg">
                실제 기업 과제를 수행한 수료생들의 <br className="block lg:hidden" />
                프로젝트 결과물을 확인해 보세요.
              </p>
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className={`${styles.section5_slider}`}
            >
              <ProjectOutputSliderItem contents={projectOutputList} />
            </div>
          </div>
        </LazyLoadSection>

        {/* Section9_취업혜택 */}
        <LazyLoadSection
          id="support"
          height={2720}
          mobileHeight={2794}
          className="bg-trueGray-800 pb-[104px] pt-20 lg:pb-[140px] lg:pt-36"
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="mb-[60px] flex flex-col items-center justify-center lg:mb-[100px]"
            >
              <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">취업 연계</p>
              <h4 className="mb-5 text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
                실전 경험을 쌓았다면,
                <br />
                취업은 멋사가 책임질게요
              </h4>
              <p className="text-center text-sm leading-normal text-label-assistive lg:text-lg">
                수강생에게 단계별 개별 커리어 컨설팅을 제공하고,
                <br />
                우수 수료생에게는 취업 연계 기회까지 함께 드려요.
              </p>
            </div>

            <h6
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="mb-5 text-center text-base font-bold leading-normal text-label-assistive lg:mb-10 lg:text-2xl"
            >
              기업 인턴십부터 개발자 협업 프로젝트까지,
              <br />
              “2번의 인턴십 기회”로 기업이 모셔가는 <br className="block lg:hidden" />
              인재가 되어보세요.
            </h6>

            <ul
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="flex flex-col gap-3 overflow-hidden lg:flex-row lg:items-stretch lg:gap-6"
            >
              <BenefitAccordion />
            </ul>

            <p
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="mt-5 text-center text-sm leading-normal text-label-assistive lg:mt-10 lg:text-base"
            >
              *교육생 훈련 상황 및 회사 사정에 따라 지원 내용이 <br className="block lg:hidden" />
              변동 될 수 있습니다.
            </p>

            <h6
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="200"
              className="mb-8 mt-20 text-center text-base font-bold text-label-assistive lg:mb-[52px] lg:mt-[140px] lg:text-2xl"
            >
              취업 지원 프로그램 구성
            </h6>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
            >
              <ProgramTab programDescList={programDescList} />

              <p className="mt-5 text-center text-xs leading-normal text-label-assistive lg:mt-10 lg:text-base">
                * 교육생 훈련 상황 및 회사 사정에 따라 지원 내용이{' '}
                <br className="block lg:hidden" />
                변동 될 수 있습니다.
              </p>
            </div>

            <h6
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="200"
              className="mb-8 mt-20 text-center text-base font-bold text-label-assistive lg:mb-[52px] lg:mt-[140px] lg:text-2xl"
            >
              취업 상담, 이렇게 진행돼요!
            </h6>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="flex flex-col gap-6 overflow-hidden rounded-lg bg-[#333] px-4 py-6 lg:flex-row lg:gap-10 lg:p-8"
            >
              <div className="w-full overflow-hidden rounded lg:w-[458px]">
                <Image
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section9_progress_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  layout="fill"
                  objectFit="cover"
                  className="!relative"
                  alt="-"
                />
              </div>
              <div className="flex flex-row items-center justify-start">
                <p className="text-sm leading-normal text-label-assistive lg:text-xl">
                  부트캠프 수강 중에는 이력서 피드백 프로그램을 진행하고 수료 후에는{' '}
                  <br className="hidden lg:block" />
                  면접 준비 지원 1:1 상담 프로그램과 더불어 부트캠프 홈페이지 내 온라인{' '}
                  <br className="hidden lg:block" />
                  기반의 진로/취업 준비 관련 상담이 진행돼요. <br className="hidden lg:block" />
                  멋쟁이사자처럼은 수료 후에도 여러분의 취업 여정을 끝까지 함께해요!
                </p>
              </div>
            </div>
          </div>
        </LazyLoadSection>

        {/* 수료생 합격 기업 모음 */}
        <LazyLoadSection
          height={433}
          mobileHeight={255}
          className="bg-white py-9 lg:pb-[106px] lg:pt-16"
        >
          <div className="mx-auto w-full">
            <h6
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="300"
              className="mb-8 text-center text-base font-semibold text-label-normal lg:mb-16 lg:text-2xl lg:font-bold"
            >
              수료생 합격 기업 모음
            </h6>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="800"
              data-aos-delay="600"
              className={`${styles.company_logo_flow_slider_wrap} flex flex-col overflow-hidden`}
            >
              <div className={`${styles.company_logo_flow_slider}`}>
                <div className={`${styles.company_logo_flow_slider_track}`}>
                  {[...firstCompanyList, ...firstCompanyList, ...firstCompanyList].map(
                    (item, idx) => (
                      <div key={`${item}_${idx}`} className={styles.slider_item}>
                        <img
                          className="h-full w-full object-contain"
                          src={item}
                          alt="company_logo"
                        />
                      </div>
                    ),
                  )}
                </div>
              </div>

              <div
                className={`${styles.company_logo_flow_slider} ${styles.company_logo_flow_slider_second}`}
              >
                <div className={`${styles.company_logo_flow_slider_track}`}>
                  {[...secondCompanyList, ...secondCompanyList, ...secondCompanyList].map(
                    (item, idx) => (
                      <div key={`${item}_${idx}`} className={styles.slider_item}>
                        <img
                          className="h-full w-full object-contain"
                          src={item}
                          alt="company_logo"
                        />
                      </div>
                    ),
                  )}
                </div>
              </div>
            </div>
          </div>
        </LazyLoadSection>

        {/* Section7_데일리 회고 */}
        <LazyLoadSection
          height={1336}
          mobileHeight={1171}
          className="bg-trueGray-800 pb-[104px] pt-20 lg:py-[180px] lg:pt-36"
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="mb-10 flex flex-col items-center justify-center lg:mb-20"
            >
              <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
                혼자 복습하지 않아요, <br className="block lg:hidden" />
                매일 함께 돌아보고
                <br />
                상호 코드 리뷰를 통해 성장해요
              </h4>
            </div>

            <div className="mx-auto flex max-w-[328px] flex-col items-center justify-between lg:max-w-[762px]">
              <div
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="300"
                className="relative mr-auto w-full lg:w-[500px]"
              >
                <Image
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section7_bubble1_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  layout="fill"
                  objectFit="contain"
                  className="!relative"
                  alt="”수업 내용이 어려워 따라가기가 어려웠어요”"
                />
              </div>
              <div
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="500"
                className="relative ml-auto mt-4 w-full lg:-mt-[21px] lg:w-[500px]"
              >
                <Image
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section7_bubble2_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  layout="fill"
                  objectFit="contain"
                  className="!relative"
                  alt="“배울 때는 알겠는데, 다음 날이 되면 잊어버려요”"
                />
              </div>
            </div>

            <h6
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="mb-20 mt-9 text-center text-sm font-bold leading-normal text-white lg:mb-[100px] lg:mt-10 lg:text-lg lg:font-semibold"
            >
              이런 고민들, 이제 하지 않아도 돼요, <br className="block lg:hidden" />
              실무에서는 ‘배운 날 복습’이 기본!
              <br />
              강사님과 함께하는 데일리 회고와 AI 툴을 활용한 <br className="block lg:hidden" />
              코드 분석 및 리팩토링까지 경험해 보세요.
            </h6>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="flex flex-col overflow-hidden rounded-lg lg:flex-row"
            >
              <div className="relative w-full lg:w-1/2 lg:flex-1">
                <Image
                  src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section7_img1_${
                    isMobile ? 'm' : 'pc'
                  }.png`}
                  layout="fill"
                  objectFit="contain"
                  className="!relative"
                  alt="”수업 내용이 어려워 따라가기가 어려웠어요”"
                />
              </div>
              <div className="flex w-full flex-col items-center justify-center bg-[#464646] px-4 py-4 lg:w-1/2 lg:flex-1 lg:px-12 lg:py-0">
                <ul className="flex w-full flex-col">
                  <li className="border-t border-[#525252] py-2 first-of-type:border-0 first-of-type:pt-0 last-of-type:pb-0 lg:py-3">
                    <p
                      className={`${styles.text_dot} ${styles.text_dot_section7} text-sm leading-normal text-white lg:text-base`}
                    >
                      하루 동안 학습한 내용을 되돌아보며 상호 코드 리뷰를 할 수 있는{' '}
                      <br className="hidden lg:block" />
                      데일리 회고 시간이 매일 수업 후 진행돼요
                    </p>
                  </li>
                  <li className="border-t border-[#525252] py-2 first-of-type:border-0 first-of-type:pt-0 last-of-type:pb-0 lg:py-3">
                    <p
                      className={`${styles.text_dot} ${styles.text_dot_section7} text-sm leading-normal text-white lg:text-base`}
                    >
                      강사님, 멘토진, 동료들과 함께 코드를 리뷰하고 IntelliJ AI Assistant를{' '}
                      <br className="hidden lg:block" />
                      활용해 코드 분석과 리팩토링까지 실시간으로 진행해요
                    </p>
                  </li>
                  <li className="border-t border-[#525252] py-2 first-of-type:border-0 first-of-type:pt-0 last-of-type:pb-0 lg:py-3">
                    <p
                      className={`${styles.text_dot} ${styles.text_dot_section7} text-sm leading-normal text-white lg:text-base`}
                    >
                      단순히 ‘과제를 제출하는 방식’이 아니라, 현업 개발자처럼 생각하고 문제를{' '}
                      <br className="hidden lg:block" />
                      해결하는 힘을 기를 수 있도록 도와드려요
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </LazyLoadSection>

        {/* Section3_핵심포인트 */}
        <LazyLoadSection
          height={1377}
          mobileHeight={1216}
          className="bg-gray-900 pb-[108px] pt-[88px] lg:pb-[180px] lg:pt-36"
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="mb-12 flex flex-col items-center justify-center gap-5 lg:mb-16"
            >
              <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
                멋쟁이사자처럼은 달라요!
              </h4>
              <p className="text-center text-sm leading-normal text-label-assistive lg:text-lg">
                타 교육과는 확실히 다른 실무 중심 커리큘럼과 <br className="block lg:hidden" />
                취업 연계, <br className="hidden lg:block" />
                그리고 AI 활용까지 더해진 <br className="block lg:hidden" />
                Java 교육을 직접 경험해 보세요.
              </p>
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
            >
              <div className="flex flex-row overflow-hidden rounded-t-lg">
                <div className="flex-1 bg-strong px-3 py-8 pb-5 lg:px-[42px] lg:pb-8">
                  <h6 className="flex h-[54px] items-center justify-center text-center text-lg font-bold leading-normal text-label-neutral lg:h-auto lg:text-2xl">
                    타 부트캠프
                  </h6>
                </div>
                <div className="flex-1 bg-[#FFEDD5] px-3 py-8 pb-5 lg:px-[42px] lg:pb-8">
                  <h6 className="flex h-[54px] items-center justify-center text-center text-lg font-bold leading-normal text-label-normal lg:h-auto lg:text-2xl">
                    멋쟁이사자처럼 <br className="block lg:hidden" />
                    백엔드 Java 17기
                  </h6>
                </div>
              </div>

              {learningSupportList.map((comparison, idx) => (
                <LearningSupportComparison
                  contents={comparison}
                  key={comparison.left.title}
                  idx={idx}
                />
              ))}
              <div className="flex flex-row overflow-hidden rounded-b-lg">
                <div className="flex-1 bg-strong pb-4"></div>
                <div className="flex-1 bg-[#FFEDD5] pb-4"></div>
              </div>
            </div>
          </div>
        </LazyLoadSection>

        {/* Section10_사전설명회 */}
        {isAfterEarlySessionDate ? (
          <LazyLoadSection
            className="bg-[#FFF5EF] pb-24 pt-20 lg:pb-40 lg:pt-36"
            height={1145}
            mobileHeight={469}
          >
            <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
              <div
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="100"
                className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-16 lg:gap-4"
              >
                <h4 className="text-center text-2xl font-bold text-label-normal lg:text-4xl">
                  사전설명회 다시보기
                </h4>
                <p className="text-center text-sm leading-normal text-label-normal lg:text-lg">
                  실시간 라이브로 진행된 사전설명회 다시보기
                </p>
              </div>
              <div
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="300"
                className="flex flex-col"
              >
                <div
                  className="h-auto overflow-hidden"
                  style={{
                    position: 'relative',
                    paddingTop: '56.25%',
                  }}
                >
                  <ReactPlayer
                    alt="멋쟁이사자처럼 사전설명회 LIVE"
                    url="https://youtube.com/live/V54IE6OiVrI?feature=share"
                    controls={true}
                    width="100%"
                    height="100%"
                    muted
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                    }}
                  />
                </div>
              </div>
            </div>
          </LazyLoadSection>
        ) : (
          <LazyLoadSection
            className="bg-[#FFF5EF] pb-24 pt-20 lg:pb-40 lg:pt-36"
            height={680}
            mobileHeight={626}
          >
            <div className="mx-auto w-full max-w-xl px-4 lg:flex lg:max-w-7xl lg:flex-row lg:items-center lg:justify-between lg:gap-6 lg:px-6">
              <div
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="100"
                className="lg:w-1/2 lg:min-w-[400px] lg:max-w-[459px]"
              >
                <div className="flex w-full flex-col items-center justify-center lg:items-start">
                  <h4 className="mb-5 text-center text-2xl font-bold leading-tight text-label-normal lg:mb-6 lg:text-left lg:text-4xl">
                    사전설명회
                  </h4>
                  <p className="mb-5 text-center text-sm leading-normal text-label-normal lg:mb-2 lg:text-left lg:text-lg">
                    백엔드 Java 17기에 궁금한 점이 있다면 실시간 온라인
                    <br />
                    사전설명회를 통해 직접 물어보고 확인해 보세요!
                  </p>
                  <div className="mb-8 w-full overflow-hidden rounded lg:hidden">
                    <img
                      src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section10_img1.gif"
                      className="h-full w-full object-contain"
                      alt="*참고용 사전설명회 진행 모습"
                    />
                  </div>
                  <p className="text-center text-sm font-bold leading-normal text-yellow-450 lg:text-left lg:text-lg">
                    일정 : 05월 08일 오후 7시
                    <br />
                    장소 : Youtube 라이브 스트리밍
                  </p>
                </div>
                <div className="mt-10 lg:mt-[60px]">
                  <Link
                    href="https://likelion2.typeform.com/bebj17typeform"
                    target="_blank"
                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-yellow-450 py-3.5 text-sm font-bold text-white lg:max-w-[396px] lg:gap-2.5 lg:py-5 lg:text-lg"
                  >
                    사전설명회 신청하기
                    <img
                      className="h-4 w-4 lg:h-[22px] lg:w-[22px]"
                      src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-design-5th/pre-orientation_icon.png"
                      alt="사전설명회"
                    />
                  </Link>
                </div>
              </div>
              <div
                data-aos-once="true"
                data-aos="fade-up"
                data-aos-easing="ease"
                data-aos-duration="400"
                data-aos-delay="300"
                className="hidden h-[376px] overflow-hidden rounded-lg lg:inline-block"
              >
                <img
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section10_img1.gif"
                  className="h-full w-full object-cover"
                  alt="*참고용 사전설명회 진행 모습"
                />
              </div>
            </div>
          </LazyLoadSection>
        )}

        {/* Section11_강사진 */}
        <LazyLoadSection
          id="teacher"
          height={1531}
          mobileHeight={1530}
          className="bg-gray-900 py-20 lg:py-[140px]"
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-20"
            >
              <p className="text-sm font-bold text-yellow-450 lg:text-lg">강사진</p>
              <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
                실제 현업에서 쓰는 방식으로 <br className="hidden lg:block" />
                Java&Spring 실무 역량을 <br className="block lg:hidden" />
                탄탄히 다져드려요
              </h4>
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="flex flex-col gap-4 overflow-hidden rounded-lg bg-trueGray-800 p-4 lg:gap-6 lg:p-6"
            >
              <div className="flex flex-col items-center lg:flex-row lg:gap-11">
                <dl className="mb-6 flex w-full flex-col gap-0.5 lg:hidden">
                  <dt className="text-sm font-bold text-yellow-450">주강사</dt>
                  <dd className="text-xl font-bold text-white">홍성현 강사님</dd>
                </dl>
                <div className="w-full overflow-hidden rounded lg:w-1/2 lg:max-w-[656px] lg:flex-1">
                  <Image
                    src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section11_img1_renew_${
                      isMobile ? 'm' : 'pc'
                    }.png`}
                    layout="fill"
                    objectFit="cover"
                    className="!relative"
                    alt="홍성현 강사님 이미지"
                  />
                </div>
                <div className="flex w-full flex-col gap-5 lg:w-auto lg:flex-1">
                  <dl className="hidden flex-col gap-2 lg:flex">
                    <dt className="text-xl font-bold text-yellow-450">주강사</dt>
                    <dd className="text-2xl font-bold text-white">홍성현 강사님</dd>
                  </dl>
                  <div className="mt-4 border-t border-label-neutral pt-4 lg:mt-0 lg:pt-5">
                    <h6 className="mb-0.5 text-sm font-bold text-white lg:mb-5 lg:text-lg lg:font-normal">
                      이력
                    </h6>
                    <ul className="flex flex-col gap-0.5 lg:gap-2">
                      <li
                        className={`${styles.text_dot} ${styles.text_dot_teacher} text-sm leading-normal text-white lg:text-base lg:text-label-assistive`}
                      >
                        현) 주식회사 웨어러블록 대표
                      </li>
                      <li
                        className={`${styles.text_dot} ${styles.text_dot_teacher} text-sm leading-normal text-white lg:text-base lg:text-label-assistive`}
                      >
                        전) KT 그룹사 클라우드 운영팀
                      </li>
                      <li
                        className={`${styles.text_dot} ${styles.text_dot_teacher} text-sm leading-normal text-white lg:text-base lg:text-label-assistive`}
                      >
                        소프트웨어 외주 개발 및 기술 컨설팅, <br className="block lg:hidden" />
                        마포구 온라인 학습 플랫폼 개발, <br />
                        개인데이터 통합관리 시스템 개발, <br className="block lg:hidden" />
                        탄소배출 모니터링 시스템 개발, <br />
                        ESG 지속가능성 분석 플랫폼 개발, <br className="block lg:hidden" />
                        이벤트 출결관리 솔루션 등 다수 프로젝트 참여
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              <dl className="flex flex-col gap-1 rounded bg-[#333] p-4 lg:gap-2 lg:px-6 lg:py-5">
                <dt className="text-sm font-bold text-label-assistive lg:text-base">
                  강사님 한마디
                </dt>
                <dd className="text-sm text-label-assistive lg:text-base">
                  최근 AI 기술의 급격한 발전과 함께, 우리 생활과 개발자 시장 모두 빠르게 변화하고
                  있습니다. 수업에서는 기본적인 백엔드 지식은 물론, 최신 트렌드를 반영한 AI 기반
                  개발 기법을 체계적으로 배우실 수 있습니다. 실무에서 바로 적용 가능한 혁신적인 학습
                  방법과 전략으로, 여러분 각자의 목표 달성을 돕겠습니다. 함께 성장하며 미래를
                  준비하는 개발자로 거듭나실 수 있도록 변함없는 열정으로 지원하겠습니다.
                </dd>
              </dl>
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="500"
              className="mt-[60px] flex flex-col gap-4 overflow-hidden rounded-lg bg-trueGray-800 p-4 lg:mt-[100px] lg:gap-6 lg:p-6"
            >
              <div className="flex flex-col items-center lg:flex-row lg:gap-11">
                <dl className="mb-6 flex w-full flex-col gap-0.5 lg:hidden">
                  <dt className="text-sm font-bold text-yellow-450">보조강사</dt>
                  <dd className="text-lg font-bold text-white">
                    1년 이상의 백엔드 Java 실무 경험을 <br className="block lg:hidden" />
                    가진 멘토진이 상주하며, 모르고 넘어가는 <br className="block lg:hidden" />
                    내용이 없도록 도와드려요.
                  </dd>
                </dl>
                <div className="w-full overflow-hidden rounded lg:w-1/2 lg:max-w-[656px] lg:flex-1">
                  <Image
                    src={`https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendj-17th/section11_img2_${
                      isMobile ? 'm' : 'pc'
                    }.png`}
                    layout="fill"
                    objectFit="cover"
                    className="!relative"
                    alt="-"
                  />
                </div>
                <div className="flex w-full flex-col gap-5 lg:w-auto lg:flex-1">
                  <dl className="hidden flex-col gap-2 lg:flex">
                    <dt className="text-xl font-bold text-yellow-450">보조강사</dt>
                    <dd className="text-2xl font-bold text-white">
                      1년 이상의 백엔드 Java 실무 경험을 가진 멘토진이{' '}
                      <br className="hidden lg:block" />
                      상주하며, 모르고 넘어가는 내용이 없도록 도와드려요.
                    </dd>
                  </dl>
                  <div className="mt-4 border-t border-label-neutral pt-4 lg:mt-0 lg:pt-5">
                    <ul className="flex flex-col gap-0.5 lg:gap-2">
                      <li
                        className={`${styles.text_dot} ${styles.text_dot_teacher} text-sm leading-normal text-white lg:text-base lg:text-label-assistive`}
                      >
                        회고 시간에 서로의 코드를 리뷰하고 피드백할 수 있는 시간을 가지며{' '}
                        <br className="hidden lg:block" />
                        멘토진의 첨삭 진행
                      </li>
                      <li
                        className={`${styles.text_dot} ${styles.text_dot_teacher} text-sm leading-normal text-white lg:text-base lg:text-label-assistive`}
                      >
                        야간 멘토 별도 운영
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </LazyLoadSection>

        {/* Section12_수료생후기 */}
        <LazyLoadSection
          height={794}
          mobileHeight={554}
          className="bg-trueGray-800 pb-24 pt-20 lg:pb-36 lg:pt-[120px]"
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="mb-10 flex flex-col items-center justify-center gap-2 lg:mb-20"
            >
              <p className="text-sm font-bold text-yellow-450 lg:text-lg">수료생 후기</p>
              <h4 className="text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
                수료생 후기 모음
              </h4>
            </div>
          </div>
          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="300"
            className={`${styles.section12_slider}`}
          >
            <ReviewSliderItem contents={reviewList} />
          </div>
        </LazyLoadSection>

        {/* Section13_교육혜택 */}
        <LazyLoadSection
          height={827}
          mobileHeight={722}
          className="bg-[#333333] py-20 lg:py-40 lg:pt-36"
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="mb-10 flex flex-col items-center justify-center lg:mb-16"
            >
              <p className="mb-2 text-sm font-bold text-yellow-450 lg:text-lg">교육 혜택</p>
              <h4 className="mb-5 text-center text-2xl font-bold leading-snug text-white lg:text-4xl lg:leading-[1.4]">
                백엔드에 대한 열정만 <br className="block lg:hidden" />
                있다면 충분해요
              </h4>
              <p className="text-center text-sm leading-normal text-label-assistive lg:text-lg">
                멋쟁이사자처럼이 준비한 다양한 교육 혜택을 통해
                <br />
                여러분은 오직 교육에만 집중할 수 있도록 도와드릴게요.
              </p>
            </div>

            <ul
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="hidden w-full flex-row gap-3.5 lg:flex"
            >
              {benefitSlideList.map(benefit => (
                <li
                  className="flex flex-1 flex-col overflow-hidden rounded-lg bg-trueGray-800"
                  key={benefit.title}
                >
                  <div className="relative w-full">
                    <Image
                      src={benefit.imgUrlPc}
                      layout="fill"
                      objectFit="contain"
                      className="!relative"
                      alt="-"
                    />
                  </div>
                  <div className="flex flex-col gap-2 p-4">
                    <h6 className="text-base font-bold leading-normal text-white">
                      {benefit.title}
                    </h6>
                    <p className="text-sm leading-normal text-white">{benefit.desc}</p>
                    {benefit.option && <p className="text-xs text-[#808080]">{benefit.option}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </div>

          <div
            data-aos-once="true"
            data-aos="fade-up"
            data-aos-easing="ease"
            data-aos-duration="400"
            data-aos-delay="100"
            className={`${styles.benefit_slider} lg:hidden`}
          >
            <BenefitSliderItem contents={benefitSlideList} />
          </div>
        </LazyLoadSection>

        {/* section14_지원안내 */}
        <LazyLoadSection
          id="condition"
          height={583}
          mobileHeight={735}
          className="bg-[#FFF2E9] py-20 lg:pb-40 lg:pt-36"
        >
          <div className="mx-auto flex w-full max-w-xl flex-col gap-20 px-4 lg:mx-auto lg:max-w-7xl lg:flex-row lg:gap-6 lg:px-6">
            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="100"
              className="w-full lg:w-auto lg:flex-1"
            >
              <div className="mb-6 lg:mb-8">
                <h4 className="text-center text-2xl font-bold text-label-normal lg:text-left lg:text-3xl">
                  지원 및 선발 안내
                </h4>
              </div>

              <div className="flex flex-col gap-3 lg:min-h-[211px] lg:gap-0 lg:rounded-lg lg:bg-white lg:px-6 lg:py-5">
                <ul className="rounded-lg bg-white px-4 lg:rounded-b-none lg:px-0">
                  <li className="flex flex-col gap-2 py-3 lg:mb-3 lg:flex-row lg:items-center lg:justify-center lg:gap-5 lg:border-b-[0.5px] lg:border-[#E0DFDE] lg:py-0 lg:pb-3">
                    <b className="w-full text-sm font-semibold leading-normal text-yellow-450 lg:w-[75px] lg:text-base lg:font-bold">
                      모집 기간
                    </b>
                    <p className="flex-1 whitespace-pre-line text-sm text-label-normal lg:whitespace-normal lg:text-base">
                      {applyBottomStart} ~ {applyBottomEnd}
                    </p>
                  </li>
                </ul>

                {applyScheduleList.map(apply => (
                  <ul className="rounded-lg bg-white px-4 lg:rounded-b-none lg:px-0">
                    <li
                      className="flex flex-col gap-2 py-3 lg:flex-row lg:items-center lg:justify-center lg:gap-5 lg:py-0"
                      key={apply.title}
                    >
                      <b className="w-full text-sm font-semibold leading-normal text-yellow-450 lg:w-[75px] lg:text-base lg:font-bold">
                        {apply.title}
                      </b>
                      <p className="flex-1 whitespace-pre-line text-sm text-label-normal lg:whitespace-normal lg:text-base">
                        {apply.content}
                      </p>
                    </li>
                  </ul>
                ))}
              </div>
            </div>

            <div
              data-aos-once="true"
              data-aos="fade-up"
              data-aos-easing="ease"
              data-aos-duration="400"
              data-aos-delay="300"
              className="w-full lg:w-auto lg:flex-1"
            >
              <div className="mb-6 lg:mb-8">
                <h4 className="text-center text-2xl font-bold text-label-normal lg:text-left lg:text-3xl">
                  교육 일정 안내
                </h4>
              </div>

              <ul className="rounded-lg bg-white px-5 py-4 lg:px-6 lg:py-5">
                <li className="flex flex-row items-center justify-center gap-5 border-b-[0.5px] border-[#E0DFDE] py-2 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:gap-8 lg:py-3">
                  <h6 className="w-[53px] shrink-0 text-sm font-semibold leading-normal text-yellow-450 lg:w-[60px] lg:text-base lg:font-bold">
                    OT&개강
                  </h6>
                  <p className="flex-1 whitespace-pre-line text-sm text-label-normal lg:whitespace-normal lg:text-base">
                    05월 28일(수) 오후 1시
                  </p>
                </li>
                <li className="flex flex-row items-center justify-center gap-5 border-b-[0.5px] border-[#E0DFDE] py-2 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:gap-8 lg:py-3">
                  <h6 className="w-[53px] shrink-0 text-sm font-semibold leading-normal text-yellow-450 lg:w-[60px] lg:text-base lg:font-bold">
                    교육 기간
                  </h6>
                  <p className="flex-1 whitespace-pre-line text-sm text-label-normal lg:whitespace-normal lg:text-base">
                    {startBottom} ~ <br className="block lg:hidden" />
                    {endBottom}
                  </p>
                </li>
                <li className="flex flex-row items-center justify-center gap-5 border-b-[0.5px] border-[#E0DFDE] py-2 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:gap-8 lg:py-3">
                  <h6 className="w-[53px] shrink-0 text-sm font-semibold leading-normal text-yellow-450 lg:w-[60px] lg:text-base lg:font-bold">
                    교육 시간
                  </h6>
                  <p className="flex-1 whitespace-pre-line text-sm text-label-normal lg:whitespace-normal lg:text-base">
                    {totalHours}시간 / 평일 9시 ~ 18시 <br className="block lg:hidden" />
                    (점심시간 12시~13시 제외)
                  </p>
                </li>
                <li className="flex flex-row items-center justify-center gap-5 border-b-[0.5px] border-[#E0DFDE] py-2 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:gap-8 lg:py-3">
                  <h6 className="w-[53px] shrink-0 text-sm font-semibold leading-normal text-yellow-450 lg:w-[60px] lg:text-base lg:font-bold">
                    학습 형태
                  </h6>
                  <p className="flex-1 whitespace-pre-line text-sm text-label-normal lg:whitespace-normal lg:text-base">
                    실시간 온라인 교육
                  </p>
                </li>
              </ul>
            </div>
          </div>
        </LazyLoadSection>

        {/* section15_자주묻는질문 */}
        <LazyLoadSection
          height={1188}
          mobileHeight={1032}
          className="bg-white pb-[120px] pt-20 lg:py-[120px]"
        >
          <div className="mx-auto w-full max-w-xl px-4 lg:max-w-7xl lg:px-6">
            <h4 className="mb-4 text-center text-2xl font-bold text-label-normal lg:mb-6 lg:text-left lg:text-4xl">
              자주 묻는 질문
            </h4>
            <ul className="mx-auto mb-10 flex max-w-[324px] flex-wrap items-center justify-center gap-1.5 lg:mb-16 lg:max-w-none lg:flex-nowrap lg:justify-start lg:gap-2">
              {bootCampFaqTabList.map(tabItem => {
                const isActive = tabItem.value === faqTab
                return (
                  <li
                    className={`${
                      isActive
                        ? 'bg-yellow-450 text-white'
                        : 'border border-normal bg-white text-gray-400'
                    } cursor-pointer rounded-full px-5 py-1.5 text-xs font-bold lg:px-6 lg:py-2 lg:text-sm`}
                    onClick={() => onClickFaqTab(tabItem.value)}
                    key={tabItem.value}
                  >
                    {tabItem.label}
                  </li>
                )
              })}
            </ul>
            <ul className="mx-auto flex flex-col">
              {faqList.map(faq => (
                <li
                  className={`${styles.faqItem} border-b border-[#E5E5E5] py-4 first-of-type:pt-0 last-of-type:border-0 last-of-type:pb-0 lg:py-6`}
                  key={faq.question}
                >
                  <details className="group overflow-hidden">
                    <summary className="cursor-pointer list-none group-open:pb-1 lg:group-open:pb-2">
                      <div className="flex w-full items-center justify-between gap-4">
                        <div>
                          <p
                            className={`${styles.faq_item_title} text-sm font-semibold text-label-normal lg:text-lg`}
                          >
                            {faq.question}
                          </p>
                        </div>
                        <ChevronDownIcon className="w-4 shrink-0 text-gray-500 group-open:rotate-180 lg:w-6" />
                      </div>
                    </summary>
                    <div className="pl-5 lg:pl-[22px]">
                      <div className="whitespace-pre-wrap text-sm leading-normal text-gray-500 lg:text-base">
                        {faq.answer && (
                          <span className="max-w-full">
                            {faq.answer}
                            {faq.externalUrl1 && (
                              <>
                                {' '}
                                자세한 발급 과정은 아래 링크를 통해 확인 가능합니다.
                                <br />-{' '}
                                <Link href={faq.externalUrl1} target="_blank" className="underline">
                                  {' '}
                                  {faq.urlName1}
                                </Link>
                                {faq.secondAnswer}
                              </>
                            )}
                            {faq.externalUrl2 && (
                              <>
                                <br />-{' '}
                                <Link href={faq.externalUrl2} target="_blank" className="underline">
                                  {' '}
                                  {faq.urlName2}
                                </Link>
                              </>
                            )}
                          </span>
                        )}
                        {faq.answerList &&
                          faq.answerList.map(el => (
                            <p key={el} className="text-sm text-gray-500 lg:text-base">
                              {el}
                            </p>
                          ))}
                      </div>
                    </div>
                  </details>
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-col items-center justify-between gap-3 rounded-md bg-[#FFF3EB] px-3 pb-5 pt-4 lg:mt-16 lg:flex-row lg:items-center lg:rounded-lg lg:px-6 lg:py-5 lg:pl-16">
              <p className="w-full text-center text-base font-semibold text-label-normal lg:w-auto lg:flex-1 lg:text-left lg:text-lg lg:font-bold">
                더 궁금한 점이 있다면 언제든지 <br className="block lg:hidden" />
                교육 매니저에게 물어보세요!
              </p>
              <Link
                href="https://likelion.channel.io/workflows/742686"
                target="_blank"
                className="flex h-[36px] w-full shrink-0 cursor-pointer flex-row items-center justify-center gap-2 rounded bg-white lg:h-[64px] lg:w-[369px] lg:rounded-lg"
              >
                <span className="text-base font-bold text-yellow-450 lg:text-lg">1:1 문의하기</span>
                <img
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/icon_go_link.png"
                  className="h-[18px] w-[18px] shrink-0 object-contain lg:h-4 lg:w-4"
                  alt="1:1 문의하기"
                />
              </Link>
            </div>

            <div className="mt-4 flex flex-col items-center justify-between gap-3 rounded-md bg-strong px-3 pb-5 pt-4 lg:mt-5 lg:flex-row lg:items-center lg:rounded-lg lg:px-6 lg:py-5 lg:pl-16">
              <p className="w-full text-center text-base font-semibold text-label-normal lg:w-auto lg:flex-1 lg:text-left lg:text-lg lg:font-bold">
                친구와 함께 백엔드 개발자라는 <br className="block lg:hidden" />
                꿈을 이뤄보세요!
              </p>
              <div
                className="flex h-[36px] w-full shrink-0 cursor-pointer flex-row items-center justify-center gap-2 rounded bg-white lg:h-[64px] lg:w-[369px] lg:rounded-lg"
                onClick={() =>
                  copyUrlShare('https://bootcamp.likelion.net/school/kdt-backendj-17th')
                }
              >
                <span className="text-base font-bold text-yellow-450 lg:text-lg">
                  링크 복사하기
                </span>
                <img
                  src="https://d35ai18pny966l.cloudfront.net/course/kdt/kdt-backendp-16th/icon_copy_share.png"
                  className="h-[18px] w-[18px] shrink-0 object-contain lg:h-4 lg:w-4"
                />
              </div>
            </div>
          </div>
        </LazyLoadSection>
      </div>
    )
  },
)

export default Bebj17th
